import React, { useCallback, useLayoutEffect, useRef, useState } from 'react';
import { t } from 'i18next';
import { useNavigate } from 'react-router-dom';
import { useForm, Controller, SubmitHandler } from 'react-hook-form';
import AsyncSelect from 'react-select/async';
import { AxiosError, AxiosInstance } from 'axios';
import { FiUpload, FiX } from 'react-icons/fi';

import { Alert, PageHeadline } from '../../components/atoms/';

import { getFileFromInput } from '../../utils/fileValidation';
import { errorToast, successToast } from '../../utils/toasts';

import { ManagementService } from '../../services/ManagementService';
import { useApi } from '../../services/hooks/apiHook';
import { useAppSelector } from '../../hooks/hooksRedux';

import { useAuth } from '../../store/auth';
import { RootState } from '../../store';

import { errorMessageAdapter } from '../../adapters';

export type SelectedEnquiry = {
  label: string;
  value: string;
  enable_subtitle?: boolean;
};
export interface FormDataEnquiry {
  attachment?: File;
  enquiry: SelectedEnquiry;
  subject: string;
  subtitle?: string;
}

const EnquiryScreen: React.FunctionComponent = () => {
  const [countCharacters, setCountCharacters] = useState<number>(0);
  const [fileEnquiry, setFileEnquiry] = useState<File>();
  const [selectedEnquiry, setSelectedEnquiry] = useState<SelectedEnquiry>();
  const [showMessage, setShowMessage] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);

  const navigate = useNavigate();

  const { token } = useAuth();
  const { legalPersonalitySlug } = useAppSelector((state: RootState) => state.language);
  const { colors } = useAppSelector((state: RootState) => state.styleGuide.data);

  const api: AxiosInstance = useApi();

  const headers = {
    Authorization: `Bearer ${token}`,
    'Content-Language': legalPersonalitySlug,
    'Content-Type': 'multipart/form-data'
  };

  const {
    register,
    handleSubmit,
    formState: { errors },
    control,
    setValue,
    setError
  } = useForm<FormDataEnquiry>({});

  const registerOptions = {
    enquiry: { required: t('validations.valEnquirySelect') },
    subject: {
      required: t('validations.valEnquiryMessage'),
      maxLength: {
        value: 5000,
        message: t('validations.valEnquiryMaxMessage')
      }
    }
  };

  const subjectsCacheRef = useRef<{ options: SelectedEnquiry[] | null; timestamp: number }>({
    options: null,
    timestamp: 0
  });

  const subjectsPromiseRef = useRef<Promise<SelectedEnquiry[] | undefined> | null>(null);
  const LANGUAGE_REFRESH_DELAY = 800;
  const languageChangeTimestampRef = useRef<number | null>(null);

  const fetchSubjects = useCallback(
    async (skipCache = false) => {
      if (languageChangeTimestampRef.current !== null) {
        const elapsed = Date.now() - languageChangeTimestampRef.current;
        if (elapsed < LANGUAGE_REFRESH_DELAY) {
          await new Promise((resolve) => setTimeout(resolve, LANGUAGE_REFRESH_DELAY - elapsed));
        }
        languageChangeTimestampRef.current = null;
      }

      const cache = subjectsCacheRef.current;

      if (!skipCache && cache.options && Date.now() - cache.timestamp < 5 * 60 * 1000) {
        return cache.options;
      }

      if (subjectsPromiseRef.current) {
        return subjectsPromiseRef.current;
      }

      const promise = ManagementService.getEnquirySubjects(api, { headers })
        .then((response) => {
          const enquiries =
            response?.data?.data?.map((item: any) => ({
              label: item.subject,
              value: item.id,
              enable_subtitle: item.enable_subtitle
            })) ?? [];

          subjectsCacheRef.current = { options: enquiries, timestamp: Date.now() };
          return enquiries;
        })
        .catch((err) => {
          console.error(err);
          errorToast(errorMessageAdapter(err as AxiosError));
          return [];
        })
        .finally(() => {
          subjectsPromiseRef.current = null;
        });

      subjectsPromiseRef.current = promise;
      return promise;
    },
    [api, headers]
  );

  const fecthGetEnquirySubjects = useCallback(
    async (_inputValue: string, callback: (enquiries: SelectedEnquiry[]) => void) => {
      const enquiries = (await fetchSubjects()) ?? [];
      callback(enquiries);
      return enquiries;
    },
    [fetchSubjects]
  );

  const handleUploadFileEnquiry = (event: any) => {
    event.persist();

    if (!event.target.files || event.target.files.length > 0) {
      const file = event.target.files?.[0];

      getFileFromInput(file)
        .then(() => {
          const maxFileSizeInMB = 5;
          const maxFileSizeInKB = 1024 * 1024 * maxFileSizeInMB;

          if (file && file.size <= maxFileSizeInKB) {
            setFileEnquiry(file);
            setShowMessage(false);
            setValue('attachment', file);
          } else {
            setShowMessage(true);
          }
        })
        .catch(function (reason) {
          setFileEnquiry(reason);
          console.log(`Error during upload ${reason}`);
          event.target.value = '';
        });
    }
  };

  const previousLanguageRef = useRef<string | undefined>(undefined);
  const selectedEnquiryValue = selectedEnquiry?.value;

  useLayoutEffect(() => {
    if (!legalPersonalitySlug) {
      return;
    }

    if (previousLanguageRef.current === undefined) {
      previousLanguageRef.current = legalPersonalitySlug;
      return;
    }

    if (previousLanguageRef.current === legalPersonalitySlug) {
      return;
    }

    previousLanguageRef.current = legalPersonalitySlug;
    subjectsCacheRef.current = { options: null, timestamp: 0 };
    subjectsPromiseRef.current = null;
    languageChangeTimestampRef.current = Date.now();

    const timeoutId = setTimeout(() => {
      void fetchSubjects(true).then((enquiries) => {
        if (!selectedEnquiryValue) {
          return;
        }

        const match = enquiries?.find((item: SelectedEnquiry) => item.value === selectedEnquiryValue);
        if (match) {
          setSelectedEnquiry(match);
          setValue('enquiry', match);
        }
      });
    }, LANGUAGE_REFRESH_DELAY);

    return () => {
      clearTimeout(timeoutId);
    };
  }, [fetchSubjects, legalPersonalitySlug, selectedEnquiryValue, setValue]);

  const handleRegistration: SubmitHandler<FormDataEnquiry> = async ({ attachment, enquiry, subject, subtitle }) => {
    setLoading(true);
    try {
      let options = {};
      if (attachment instanceof File) {
        options = {
          attachment
        };
      }

      const formData = {
        ...options,
        enquirySubjectId: `${enquiry.value}`,
        message: subject,
        subtitle: subtitle || undefined
      };

      const result = await ManagementService.saveRegisterEnquiry(api, formData, { headers });
      successToast('Enquiry send success');
      setLoading(false);

      setTimeout(() => {
        navigate('/dashboard');
      }, 1000);
      return result;
    } catch (err: any) {
      console.error(err);
      setLoading(false);
      errorToast(errorMessageAdapter(err as AxiosError));
    }
  };
  const primaryColor = colors?.primary || '#0C1FAA';

  return (
    <>
      <div className="mb-6">
        <PageHeadline title={t('titles.ttlEnquiryPage')} subtitle={t('enquiry.enquirySubtitle')} />
      </div>

      <form onSubmit={handleSubmit(handleRegistration)} className="mt-6">
        <div className="rounded-lg border-[2px] border-solid border-[#E1E9EF] bg-white p-6">
          <div className="flex flex-col gap-6">
            {/* Enquiry Subject Select */}
            <div className="flex flex-col gap-2">
              <label className="text-sm font-semibold" style={{ color: primaryColor }}>
                {t('titles.ttlEnquirySelect')} <span className="text-red-500">*</span>
              </label>
              <Controller
                control={control}
                name="enquiry"
                render={({ field: { value, onChange, ...field } }) => {
                  return (
                    <AsyncSelect
                      key={legalPersonalitySlug}
                      {...field}
                      id="enquiry"
                      value={selectedEnquiry ?? null}
                      cacheOptions
                      loadOptions={fecthGetEnquirySubjects}
                      placeholder={t('OthersSelectYourOption')}
                      onChange={(option) => {
                        const selectedOption = option as SelectedEnquiry | null;
                        if (selectedOption) {
                          setSelectedEnquiry(selectedOption);
                          onChange(selectedOption);
                        } else {
                          setSelectedEnquiry(undefined);
                          onChange(null);
                        }
                      }}
                      defaultOptions
                      styles={{
                        control: (base: any, state: any) => ({
                          ...base,
                          minHeight: '48px',
                          borderWidth: '2px',
                          borderColor: state.isFocused ? primaryColor : '#E1E9EF',
                          borderRadius: '8px',
                          boxShadow: 'none',
                          '&:hover': {
                            borderColor: primaryColor
                          }
                        }),
                        placeholder: (base: any) => ({
                          ...base,
                          color: '#727272'
                        }),
                        option: (base: any, state: any) => ({
                          ...base,
                          backgroundColor: state.isFocused ? '#F8F9FA' : 'white',
                          color: '#373D43',
                          '&:hover': {
                            backgroundColor: `${primaryColor}15`
                          }
                        })
                      }}
                    />
                  );
                }}
                rules={{
                  required: true
                }}
              />
              {errors.enquiry?.message === '' && (
                <span className="text-xs text-red-500">{t('validations.valEnquirySelect')}</span>
              )}

              {/* Conditional Subtitle Field */}
              {selectedEnquiry?.enable_subtitle === true && (
                <div className="mt-2">
                  <input
                    {...register('subtitle')}
                    id="subtitle"
                    placeholder={t('OthersOptionsSelect') || ''}
                    style={{
                      borderColor: '#E1E9EF'
                    }}
                    onFocus={(e) => (e.target.style.borderColor = primaryColor)}
                    onBlur={(e) => (e.target.style.borderColor = '#E1E9EF')}
                    className="h-[48px] w-full rounded-lg border-[2px] border-solid px-4 text-sm text-[#373D43] outline-none transition-all"
                  />
                </div>
              )}
            </div>

            {/* Message TextArea */}
            <div className="flex flex-col gap-2">
              <label className="text-sm font-semibold" style={{ color: primaryColor }}>
                {t('titles.ttlEnquiryMessage')} <span className="text-red-500">*</span>
              </label>
              <textarea
                id="subject"
                {...register('subject', registerOptions.subject)}
                onChange={(e) => setCountCharacters(e.target.value.length)}
                rows={10}
                placeholder={t('enquiry.enquiryMessagePlaceholder') || ''}
                style={{
                  borderColor: '#E1E9EF'
                }}
                onFocus={(e) => (e.target.style.borderColor = primaryColor)}
                onBlur={(e) => (e.target.style.borderColor = '#E1E9EF')}
                className="w-full rounded-lg border-[2px] border-solid p-4 text-sm text-[#373D43] outline-none transition-all"
              />
              {errors.subject?.message && (
                <span className="text-xs text-red-500">{errors.subject.message as string}</span>
              )}
              <p className="text-xs text-[#727272]">
                {countCharacters}/5000 {t('othersTextCharacter')}
              </p>
            </div>

            {/* File Upload */}
            <div className="flex flex-col gap-2">
              <label className="text-sm font-semibold" style={{ color: primaryColor }}>{t('titles.ttlEnquiryAttachment')}</label>

              {!fileEnquiry ? (
                <label 
                  className="group flex h-[240px] w-full cursor-pointer flex-col items-center justify-center gap-3 rounded-lg border-[2px] border-dashed bg-[#F8F9FA] transition-all"
                  style={{ 
                    borderColor: primaryColor,
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = `${primaryColor}15`;
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = '#F8F9FA';
                  }}
                >
                  <div 
                    className="flex h-16 w-16 items-center justify-center rounded-full transition-all"
                    style={{ backgroundColor: `${primaryColor}20` }}
                  >
                    <FiUpload style={{ color: primaryColor }} size={28} />
                  </div>
                  <div className="flex flex-col items-center gap-1">
                    <span className="text-base font-semibold" style={{ color: primaryColor }}>{t('labels.lblPushYourDocument')}</span>
                    <span className="text-sm text-[#727272]">{t('labels.lblOrSearchInYourComputer')}</span>
                    <span className="text-xs text-[#727272]">{t('labels.lblDocumentsSupportedEnquiry')}</span>
                  </div>
                  <Controller
                    control={control}
                    name={'attachment'}
                    render={({ field: { value, onChange, ...field } }) => {
                      return (
                        <input
                          {...field}
                          id="attachment"
                          type="file"
                          className="hidden"
                          accept="image/*,.pdf"
                          {...register('attachment')}
                          onChange={handleUploadFileEnquiry}
                        />
                      );
                    }}
                  />
                </label>
              ) : (
                <div className="flex items-center justify-between rounded-lg border-[2px] border-solid border-[#E1E9EF] bg-[#F8F9FA] p-4">
                  <div className="flex items-center gap-3">
                    <div 
                      className="flex h-12 w-12 items-center justify-center rounded-lg"
                      style={{ backgroundColor: `${primaryColor}20` }}
                    >
                      <FiUpload style={{ color: primaryColor }} size={20} />
                    </div>
                    <div className="flex flex-col">
                      <span className="text-sm font-semibold text-[#373D43]">{fileEnquiry.name}</span>
                      <span className="text-xs text-[#727272]">
                        {(fileEnquiry.size / 1024).toFixed(2)} KB
                      </span>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => setFileEnquiry(undefined)}
                    className="flex h-8 w-8 items-center justify-center rounded-full transition-all hover:bg-[#FEE2E2]"
                  >
                    <FiX className="text-[#DC2626]" size={20} />
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Alert Message */}
          {showMessage && (
            <div className="mt-4">
              <Alert
                typeIcon={'warning'}
                message={t('AlertEnquiryMessage')}
                show={showMessage}
                onClose={() => setShowMessage(false)}
              />
            </div>
          )}

          {/* Submit Button */}
          <div className="mt-6">
            <button
              type="submit"
              disabled={loading}
              style={{
                backgroundColor: loading ? '#94a3b8' : primaryColor,
              }}
              onMouseEnter={(e) => {
                if (!loading) {
                  e.currentTarget.style.opacity = '0.9';
                }
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.opacity = '1';
              }}
              className="w-full rounded-lg px-6 py-3 text-base font-semibold text-white transition-all disabled:cursor-not-allowed sm:w-auto sm:min-w-[200px]"
            >
              {loading ? (
                <div className="flex items-center justify-center gap-2">
                  <div className="h-5 w-5 animate-spin rounded-full border-2 border-white border-t-transparent"></div>
                  <span>{t('buttons.btnSending')}</span>
                </div>
              ) : (
                t('buttons.btnSendMessage')
              )}
            </button>
          </div>
        </div>
      </form>
    </>
  );
};

export { EnquiryScreen };
